import React, {useRef, useState} from 'react'
import {Box, Typography} from '@mui/material'
import AppDialog from 'src/components-new/feedback/AppDialog'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import SignaturePadNew, {SignaturePadHandle} from 'src/components-new/inputs/SignaturePadNew'
import Button from 'src/components-new/primitives/Button'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {PatientAcknowledgement, signatureDataUrlToFile} from './activityAckUtils'

interface ActivityAckDialogNewProps {
  activityId: string
  open: boolean
  onClose: () => void
  onConfirmed: (acknowledgement: PatientAcknowledgement) => void
}

/**
 * "Presa visione" confirmation: the signature is optional, the patient can
 * confirm straight away. Signature and confirmation are sent in one request,
 * so nothing is uploaded if the dialog is closed without confirming.
 */
export default function ActivityAckDialogNew({activityId, open, onClose, onConfirmed}: ActivityAckDialogNewProps): JSX.Element {
  const toast = useToast()
  const signaturePadRef = useRef<SignaturePadHandle>(null)
  const [hasSigned, setHasSigned] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleConfirm = async () => {
    const dataUrl = signaturePadRef.current?.getTrimmedDataUrl()
    const signature = dataUrl ? signatureDataUrlToFile(dataUrl, `Signature_${activityId}.png`) : undefined

    let rejected = false
    setSubmitting(true)
    const response = await apiClient.confirmActivityAck(activityId, signature, (status: string) => {
      if (status === STATUS.REJECTED) rejected = true
    })
    setSubmitting(false)

    if (rejected || !response) {
      toast.error('Errore nel confermare la presa visione.')
      return
    }
    toast.success('Presa visione confermata.')
    onConfirmed(response as PatientAcknowledgement)
  }

  return (
    <AppDialog
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title="Presa visione"
      maxWidth="md"
      actions={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Annulla</Button>
          <Button onClick={handleConfirm} loading={submitting} disabled={submitting}>Conferma presa visione</Button>
        </>
      )}
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        <Typography variant="body2" sx={{color: NEUTRAL_SCALE[700]}}>
          Confermando dichiari di aver preso visione del resoconto dell&apos;attività. La firma è facoltativa.
        </Typography>
        <SignaturePadNew ref={signaturePadRef} label="Firma Paziente" onChange={setHasSigned} disabled={submitting} />
        <Box>
          <Button
            variant="ghost"
            onClick={() => signaturePadRef.current?.clear()}
            disabled={!hasSigned || submitting}
          >
            Cancella firma
          </Button>
        </Box>
      </Box>
    </AppDialog>
  )
}
